import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  signIn,
  signUp,
  signOut,
  resetPassword,
  updatePassword,
  getCurrentUser,
  getSession,
  setupAuthListener,
  createMFAChallenge,
  verifyMFAChallenge,
  getMFAFactors
} from '../api/supabase';
import { userAPI } from '../api/enhancedApiService';
import { setAuthReady } from '../api/enhancedApiService';
import { getCurrentUTCSeconds } from '../utils/dateUtils';
import { secureLogger } from '../utils/secureLogger';
import { clearCache } from '../utils/cacheUtils';

/**
 * Wipe data that belongs to the previous browser user before a new
 * sign-in or sign-up establishes a session. Without this, cacheUtils
 * keeps `connections.list` (memory + localStorage) and the new user's
 * ConnectionContext returns the previous user's connection ids, which
 * 404 against the backend and force the user to manually refresh.
 *
 * Logout already removes some of these (handleLogout below) but only
 * for the explicit-logout path. Sign-in without prior logout — common
 * when test users get deleted via SQL or when sessions expire — leaves
 * the cache contaminated. Call this at the head of every auth entry.
 */
const clearStaleSessionData = () => {
  try {
    clearCache();
  } catch {
    // cacheUtils is best-effort; never block auth on a cache clear failure.
  }
  try {
    sessionStorage.removeItem('activeConnectionId');
  } catch {}
  if (typeof window !== 'undefined' && window._sessionCache) {
    window._sessionCache = null;
  }
};

// Create the auth context
const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

// Session timeout in milliseconds (30 minutes)
const SESSION_IDLE_TIMEOUT = 30 * 60 * 1000;
// Session refresh threshold (5 minutes before expiry)
const SESSION_REFRESH_THRESHOLD = 5 * 60;

// Debug toggle - set to false to reduce console noise
const DEBUG_AUTH = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactors, setMfaFactors] = useState([]);

  // Use refs to prevent infinite loops and track state
  const authListenerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const sessionCheckRef = useRef(null);
  const initializedRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const sessionRecoveryAttemptRef = useRef(0);
  const maxSessionRecoveryAttempts = 3;

  // Debug logging helper - with log levels
  const logDebug = useCallback((message, data = null, level = 'info') => {
    if (!DEBUG_AUTH) return; // Skip all debug logging if disabled

    // Only log important events, not every activity update
    if (level === 'verbose' && process.env.NODE_ENV === 'production') return;

    if (message.includes('Activity updated') && level !== 'important') {
      // Skip activity logs unless explicitly important
      return;
    }

    secureLogger.debug(`[AuthContext] ${message}`, data);
  }, []);

  // Memoize updateActivity to prevent unnecessary re-renders
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
    // Remove the verbose activity logging
    // logDebug('Activity updated', new Date(now).toISOString());
  }, []);

  // Enhanced session validation
  const validateSession = useCallback((sessionToValidate) => {
    if (!sessionToValidate) {
      logDebug('Session validation failed: no session');
      return false;
    }

    // Check if session has required properties
    if (!sessionToValidate.access_token || !sessionToValidate.user) {
      logDebug('Session validation failed: missing token or user', {
        hasToken: !!sessionToValidate.access_token,
        hasUser: !!sessionToValidate.user
      });
      return false;
    }

    // Check token expiration using UTC
    if (sessionToValidate.expires_at) {
      const nowUTC = getCurrentUTCSeconds();
      const expiresAt = sessionToValidate.expires_at;
      const timeUntilExpiry = expiresAt - nowUTC;

      logDebug('Token expiration check', {
        nowUTC,
        expiresAt,
        timeUntilExpiry,
        isExpired: timeUntilExpiry <= 0
      });

      // If token is expired or expires within 30 seconds
      if (timeUntilExpiry <= 30) {
        logDebug('Session validation failed: token expired or expiring soon');
        return false;
      }
    }

    logDebug('Session validation passed');
    return true;
  }, [logDebug]);

  // Session recovery mechanism
  const attemptSessionRecovery = useCallback(async () => {
    if (sessionRecoveryAttemptRef.current >= maxSessionRecoveryAttempts) {
      logDebug('Max session recovery attempts reached, giving up');
      return null;
    }

    sessionRecoveryAttemptRef.current++;
    logDebug(`Session recovery attempt ${sessionRecoveryAttemptRef.current}`);

    try {
      // Try to get a fresh session from Supabase
      const freshSession = await getSession();

      if (freshSession && validateSession(freshSession)) {
        logDebug('Session recovery successful');
        sessionRecoveryAttemptRef.current = 0; // Reset counter on success
        return freshSession;
      }

      logDebug('Session recovery failed: no valid session found');
      return null;
    } catch (error) {
      logDebug('Session recovery error', error);
      return null;
    }
  }, [validateSession, logDebug]);

  // Handle logout - define with useCallback to use in dependencies
  const handleLogout = useCallback(async (reason = 'manual') => {
    if (isLoggingOutRef.current) {
      logDebug('Logout already in progress, skipping');
      return;
    }

    isLoggingOutRef.current = true;
    logDebug(`Initiating logout: ${reason}`);

    try {
      // Clear timers first
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (sessionCheckRef.current) clearInterval(sessionCheckRef.current);

      // Clear state
      setUser(null);
      setSession(null);
      setError(null);

      // Clear stored data
      sessionStorage.removeItem('activeConnectionId');

      // Clear session cache
      if (window._sessionCache) {
        window._sessionCache = null;
      }

      try {
        // Sign out from Supabase
        await signOut();
      } catch (signOutError) {
        logDebug('Error during Supabase signOut', signOutError);
        // Continue with logout even if signOut fails
      }

      logDebug('Logout completed, redirecting to login');

      // Small delay to ensure state is cleared
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);

    } catch (err) {
      logDebug('Error during logout', err);
      // Force redirect even if logout fails
      window.location.href = '/login';
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [logDebug]);

  // Check for session expiration - define with useCallback to use in dependencies
  const checkSessionExpiration = useCallback(async () => {
    if (!session || isLoggingOutRef.current) return;

    try {
      logDebug('Checking session expiration');

      // First, validate the current session
      if (!validateSession(session)) {
        logDebug('Current session invalid, attempting recovery');

        // Try to recover the session
        const recoveredSession = await attemptSessionRecovery();

        if (recoveredSession) {
          logDebug('Session recovered successfully');
          setSession(recoveredSession);
          setUser(recoveredSession.user);
          updateActivity();
          return;
        }

        logDebug('Session recovery failed, logging out');
        await handleLogout('session_invalid');
        return;
      }

      // Check for idle timeout
      const timeSinceActivity = Date.now() - lastActivity;
      if (timeSinceActivity > SESSION_IDLE_TIMEOUT) {
        logDebug('Session idle timeout exceeded', {
          timeSinceActivity,
          timeout: SESSION_IDLE_TIMEOUT
        });
        await handleLogout('idle_timeout');
        return;
      }

      // Check if token needs refresh (but be more lenient)
      if (session.expires_at) {
        const nowUTC = getCurrentUTCSeconds();
        const timeUntilExpiry = session.expires_at - nowUTC;

        // Only refresh if really close to expiry (2 minutes instead of 5)
        if (timeUntilExpiry <= 2 * 60) {
          logDebug('Token needs refresh soon', { timeUntilExpiry, threshold: 2 * 60 });

          try {
            // Try to refresh the session
            const refreshedSession = await getSession();
            if (refreshedSession && validateSession(refreshedSession)) {
              logDebug('Session refreshed successfully');
              setSession(refreshedSession);
              setUser(refreshedSession.user);
              updateActivity();
            }
          } catch (refreshError) {
            logDebug('Session refresh failed', refreshError);
            // Don't logout immediately on refresh failure, wait for next check
          }
        }
      }

    } catch (err) {
      logDebug('Error checking session', err);
    }
  }, [session, lastActivity, validateSession, attemptSessionRecovery, handleLogout, updateActivity, logDebug]);

  // Set up session expiration check
  useEffect(() => {
    if (!session) return;

    // Clear any existing timer
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
    }

    // Set up check interval (every 2 minutes instead of 30 seconds)
    sessionCheckRef.current = setInterval(checkSessionExpiration, 2 * 60 * 1000);

    // Run initial check
    checkSessionExpiration();

    // Cleanup
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [session, checkSessionExpiration]);

  // Initialize auth state - only run once
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      // Prevent multiple initializations
      if (initializedRef.current) {
        logDebug('Auth already initialized, skipping');
        return;
      }

      initializedRef.current = true;
      logDebug('Initializing auth');

      try {
        // Get current session
        const currentSession = await getSession();

        if (!isMounted) return;

        logDebug('Initial session check', {
          hasSession: !!currentSession,
          hasToken: !!currentSession?.access_token,
          hasUser: !!currentSession?.user
        });

        if (currentSession && validateSession(currentSession)) {
          logDebug('Valid session found during initialization');
          setSession(currentSession);
          setUser(currentSession.user);
          updateActivity();

          // Signal that auth is ready
          setAuthReady();
          logDebug('Auth marked as ready');
        } else {
          logDebug('No valid session found during initialization');
        }
      } catch (err) {
        logDebug('Error initializing auth', err);
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          logDebug('Auth initialization completed');
        }
      }
    };

    // Set up auth state change listener
    authListenerRef.current = setupAuthListener((event, changedSession) => {
      logDebug('Auth state change detected', { event, hasSession: !!changedSession });

      if (event === 'SIGNED_OUT') {
        logDebug('Supabase SIGNED_OUT event');
        if (!isLoggingOutRef.current) {
          setUser(null);
          setSession(null);
          setError(null);
        }
      } else if (event === 'SIGNED_IN' && changedSession) {
        logDebug('Supabase SIGNED_IN event');
        if (validateSession(changedSession)) {
          setSession(changedSession);
          setUser(changedSession.user);
          updateActivity();
          sessionRecoveryAttemptRef.current = 0; // Reset recovery attempts

          // Update auth ready state
          if (changedSession.access_token) {
            setAuthReady();
          }
        } else {
          logDebug('Invalid session in SIGNED_IN event');
        }
      } else if (event === 'TOKEN_REFRESHED' && changedSession) {
        logDebug('Supabase TOKEN_REFRESHED event');
        if (validateSession(changedSession)) {
          setSession(changedSession);
          setUser(changedSession.user);
          updateActivity();
        }
      }
      // Don't change state for INITIAL_SESSION events if we already have a valid session

      setLoading(false);
    });

    // Initialize auth
    initializeAuth();

    // Set up activity tracking
    const handleActivity = () => updateActivity();

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Cleanup
    return () => {
      isMounted = false;

      // Remove event listeners
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);

      // Clear timers
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (sessionCheckRef.current) clearInterval(sessionCheckRef.current);

      // Unsubscribe from auth listener
      if (authListenerRef.current?.subscription?.unsubscribe) {
        authListenerRef.current.subscription.unsubscribe();
      }
    };
  }, [updateActivity, validateSession, logDebug]);

  // Sign in function with MFA support
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      logDebug('Login attempt started');

      // Evict the previous user's cached data BEFORE establishing the new
      // session. See clearStaleSessionData comment at top of file.
      clearStaleSessionData();

      const result = await signIn(email, password);
      const { user: authUser, session: authSession, mfaRequired: needsMfa } = result;

      logDebug('Login successful', {
        hasUser: !!authUser,
        hasSession: !!authSession,
        hasToken: !!authSession?.access_token,
        mfaRequired: needsMfa
      });

      if (needsMfa) {
        // MFA is required, get factors and set MFA state
        setMfaRequired(true);
        try {
          const factors = await getMFAFactors();
          setMfaFactors(factors?.totp || []);
        } catch (factorError) {
          logDebug('Failed to get MFA factors', factorError);
          setMfaFactors([]);
        }
        
        // Don't set user/session yet, wait for MFA completion
        return { user: authUser, session: authSession, mfaRequired: true };
      }

      if (validateSession(authSession)) {
        setUser(authUser);
        setSession(authSession);
        setMfaRequired(false);
        setMfaFactors([]);
        updateActivity();
        sessionRecoveryAttemptRef.current = 0; // Reset recovery attempts

        return { user: authUser, session: authSession, mfaRequired: false };
      } else {
        throw new Error('Invalid session received during login');
      }
    } catch (err) {
      logDebug('Login failed', err);
      setError(err.message);
      setMfaRequired(false);
      setMfaFactors([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign up function
  const register = async (email, password, userData = {}) => {
    try {
      setError(null);
      setLoading(true);
      logDebug('Registration attempt started');

      const { user: authUser, session: authSession } = await signUp(email, password, userData);

      // If email confirmation is required, session may be null
      if (authSession && validateSession(authSession)) {
        setUser(authUser);
        setSession(authSession);
        updateActivity();
        sessionRecoveryAttemptRef.current = 0; // Reset recovery attempts

        // Setup user in the database
        await userAPI.setupUser({
          user_id: authUser.id,
          email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          organization_name: userData.organization_name || '',
        });

        logDebug('Registration successful with immediate session');
      } else {
        logDebug('Registration successful, email confirmation required');
      }

      return { user: authUser, session: authSession };
    } catch (err) {
      logDebug('Registration failed', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign up function with organization creation
  const signUpWithOrganization = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      logDebug('SignUp attempt started');

      // Evict the previous user's cached data BEFORE establishing the new
      // session. Especially important here: same browser may have just held
      // an admin@/contact@ test signup whose connections were deleted via
      // SQL, leaving cached connection ids that 404 for the new user.
      clearStaleSessionData();

      const {
        email,
        password,
        firstName,
        lastName,
        organizationName,
        teamSize,
        useCase,
        selectedPlan,
        source
      } = userData;

      // Create the user account via our backend API
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          organizationName,
          selectedPlan,
          teamSize,
          useCase,
          source
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      logDebug('SignUp API succeeded', {
        hasUser: !!data.user,
        hasToken: !!data.token,
      });

      // If the backend couldn't auto-login (email verification path), bail
      // out without trying to establish a session — caller handles redirect
      // to the login page.
      if (!data.token) {
        logDebug('Email verification required (no token from backend)');
        return {
          user: data.user,
          emailVerificationRequired: true,
        };
      }

      // Backend returns only access_token + user, which is not a complete
      // Supabase session (missing refresh_token, expires_at). Setting that
      // partial object as session leaves Supabase's auth client unaware of
      // the login, so the next ProtectedRoute check bounces the user to
      // /login. Fix: do a real Supabase signInWithPassword using the
      // credentials the user just provided. The backend's auto-login is
      // now redundant but harmless — keep it so the response shape stays
      // consistent for future paths.
      const signInResult = await signIn(email, password);
      const { user: authUser, session: authSession } = signInResult;

      if (authSession && validateSession(authSession)) {
        logDebug('Establishing Supabase session after signup');
        setSession(authSession);
        setUser(authUser);
        updateActivity();
        setAuthReady();
        sessionRecoveryAttemptRef.current = 0;
        return {
          user: authUser,
          session: authSession,
          emailVerificationRequired: false,
        };
      }

      // signIn returned something we couldn't use. Treat as soft-failure:
      // signup itself worked, user can log in manually.
      logDebug('Post-signup signIn did not yield a valid session');
      return {
        user: data.user,
        session: null,
        emailVerificationRequired: false,
      };

    } catch (err) {
      logDebug('SignUp failed', err);
      setError(err.message || 'Failed to create account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const logout = async () => {
    await handleLogout('manual');
  };

  // Reset password
  const sendPasswordResetEmail = async (email) => {
    try {
      setError(null);
      logDebug('Password reset email request');
      await resetPassword(email);
    } catch (err) {
      logDebug('Password reset failed', err);
      setError(err.message);
      throw err;
    }
  };

  // Update password
  const changePassword = async (newPassword) => {
    try {
      setError(null);
      logDebug('Password change attempt');
      await updatePassword(newPassword);
      updateActivity();
    } catch (err) {
      logDebug('Password change failed', err);
      setError(err.message);
      throw err;
    }
  };

  // Complete MFA verification
  const completeMFA = async (code) => {
    try {
      setError(null);
      setLoading(true);
      logDebug('MFA verification attempt');

      if (mfaFactors.length === 0) {
        throw new Error('No MFA factors available');
      }

      const factorId = mfaFactors[0].id;

      // Create challenge
      const challengeData = await createMFAChallenge(factorId);
      const challengeId = challengeData.id;

      // Verify the code
      const verifyResult = await verifyMFAChallenge(factorId, challengeId, code);

      logDebug('MFA verify result structure', {
        hasSession: !!verifyResult.session,
        hasAccessToken: !!verifyResult.access_token,
        hasUser: !!verifyResult.user
      });

      // Handle the response structure - Supabase returns session data at root level
      const session = verifyResult.session || {
        access_token: verifyResult.access_token,
        token_type: verifyResult.token_type,
        expires_at: verifyResult.expires_at,
        expires_in: verifyResult.expires_in,
        refresh_token: verifyResult.refresh_token,
        user: verifyResult.user
      };

      if (session && validateSession(session)) {
        setUser(session.user);
        setSession(session);
        setMfaRequired(false);
        setMfaFactors([]);
        updateActivity();
        sessionRecoveryAttemptRef.current = 0;

        logDebug('MFA verification successful');
        return { user: session.user, session: session };
      } else {
        throw new Error('Invalid session received after MFA verification');
      }
    } catch (err) {
      logDebug('MFA verification failed', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cancel MFA flow
  const cancelMFA = () => {
    setMfaRequired(false);
    setMfaFactors([]);
    setError(null);
  };

  // Login with email and password after invite acceptance
  const loginAfterInvite = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      logDebug('Login after invite acceptance');

      // Evict the previous user's cached data BEFORE establishing the new
      // session. See clearStaleSessionData comment at top of file.
      clearStaleSessionData();

      const result = await signIn(email, password);
      const { user: authUser, session: authSession, mfaRequired: needsMfa } = result;

      if (needsMfa) {
        setMfaRequired(true);
        try {
          const factors = await getMFAFactors();
          setMfaFactors(factors?.totp || []);
        } catch (factorError) {
          logDebug('Failed to get MFA factors', factorError);
          setMfaFactors([]);
        }
        return { user: authUser, session: authSession, mfaRequired: true };
      }

      if (validateSession(authSession)) {
        setUser(authUser);
        setSession(authSession);
        setMfaRequired(false);
        setMfaFactors([]);
        updateActivity();
        sessionRecoveryAttemptRef.current = 0; // Reset recovery attempts

        return { user: authUser, session: authSession, mfaRequired: false };
      } else {
        throw new Error('Invalid session received during login');
      }
    } catch (err) {
      logDebug('Login after invite failed', err);
      setError(err.message);
      setMfaRequired(false);
      setMfaFactors([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    session,
    loading,
    error,
    login,
    register,
    signUp: signUpWithOrganization,
    logout,
    sendPasswordResetEmail,
    changePassword,
    loginAfterInvite,
    completeMFA,
    cancelMFA,
    mfaRequired,
    mfaFactors,
    isAuthenticated: !!user && !!session && validateSession(session),
    lastActivity,
    updateActivity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;